import { useState, useEffect, useCallback } from 'react';
import { getHierarchyTree } from '../../lib/api';
import { formatApiError, getInitials } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { CaretDown, CaretRight, TreeStructure, User } from '@phosphor-icons/react';

function flattenTree(nodes, level, expandedMap) {
  const result = [];
  for (const node of nodes) {
    const hasChildren = node.children && node.children.length > 0;
    result.push({ ...node, level, hasChildren });
    if (hasChildren && expandedMap[node.id]) {
      result.push(...flattenTree(node.children, level + 1, expandedMap));
    }
  }
  return result;
}

function countAll(nodes) {
  let c = 0;
  for (const n of nodes) {
    c += 1;
    if (n.children) c += countAll(n.children);
  }
  return c;
}

function buildDefaultExpanded(nodes, level) {
  const map = {};
  for (const n of nodes) {
    if (level < 2 && n.children && n.children.length > 0) {
      map[n.id] = true;
      Object.assign(map, buildDefaultExpanded(n.children, level + 1));
    }
  }
  return map;
}

export default function HierarchyPage() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMap, setExpandedMap] = useState({});

  useEffect(() => {
    const fetchTree = async () => {
      try {
        const res = await getHierarchyTree();
        setTree(res.data);
        setExpandedMap(buildDefaultExpanded(res.data, 0));
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    fetchTree();
  }, []);

  const toggle = useCallback((nodeId) => {
    setExpandedMap(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  }, []);

  const flatNodes = flattenTree(tree, 0, expandedMap);

  return (
    <div className="p-6 space-y-6" data-testid="hierarchy-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Organization Hierarchy
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Team structure and reporting lines
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12" />)}
        </div>
      ) : tree.length === 0 ? (
        <Card className="border-zinc-200 rounded-sm">
          <CardContent className="p-12 text-center">
            <TreeStructure size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No users found</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-zinc-200 rounded-sm">
          <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <TreeStructure size={18} className="text-[#002FA7]" />
              Team Tree
            </CardTitle>
            <div className="flex items-center gap-2">
              <User size={14} className="text-zinc-400" />
              <span className="text-sm text-zinc-500">{countAll(tree)} members</span>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-0.5">
            {flatNodes.map(node => (
              <div
                key={node.id}
                data-testid={`tree-node-${node.id}`}
                className="flex items-center gap-3 py-2.5 px-3 rounded-sm hover:bg-zinc-50 cursor-pointer transition-colors"
                style={{ marginLeft: node.level * 32 }}
                onClick={() => node.hasChildren && toggle(node.id)}
              >
                {node.hasChildren ? (
                  <button className="w-5 h-5 flex items-center justify-center text-zinc-400">
                    {expandedMap[node.id] ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  </button>
                ) : (
                  <span className="w-5" />
                )}
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold">
                    {getInitials(node.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{node.name}</span>
                    <Badge
                      variant={node.roles?.includes('admin') ? 'default' : 'secondary'}
                      className="text-[10px] capitalize shrink-0"
                    >
                      {node.roles?.length ? node.roles.map(r => r === 'account_manager' ? 'AM' : r === 'operations_manager' ? 'OM' : r).join(', ') : node.role || 'user'}
                    </Badge>
                    {node.status !== 'active' && (
                      <Badge className="bg-zinc-100 text-zinc-500 text-[10px]">{node.status}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 truncate">
                    {node.designation || node.email}
                  </p>
                </div>
                {node.hasChildren && (
                  <span className="text-xs text-zinc-400 shrink-0">
                    {node.children.length} report{node.children.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
